import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Users, TrendingUp, Coins, PercentIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

type ReferralStatus = "PENDING" | "CONVERTED" | "EXPIRED";

function statusBadge(status: ReferralStatus) {
  if (status === "CONVERTED") return <Badge className="bg-success/10 text-success border-success/20">Converti</Badge>;
  if (status === "EXPIRED") return <Badge variant="secondary" className="bg-destructive/10 text-destructive border-destructive/20">Expiré</Badge>;
  return <Badge variant="outline" className="text-warning border-warning/30">En attente</Badge>;
}

function formatDate(d: string | Date | null | undefined) {
  if (!d) return "—";
  return format(new Date(d), "dd/MM/yyyy", { locale: fr });
}

export default function TravauxBTPReferrals() {
  const [statusFilter, setStatusFilter] = useState<ReferralStatus | "ALL">("ALL");
  const [page, setPage] = useState(1);
  const LIMIT = 20;

  const { data, isLoading, isFetching } = trpc.admin.travauxbtp.listReferrals.useQuery({
    page,
    limit: LIMIT,
    status: statusFilter !== "ALL" ? statusFilter : undefined,
  });

  const items = data?.items ?? [];
  const stats = data?.stats;
  const totalPages = data?.totalPages ?? 1;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Programme parrainage pro</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Suivi des parrainages artisans TravauxBTP — conversions et crédits versés
          </p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total parrainages</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total ?? "—"}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Convertis</CardTitle>
              <TrendingUp className="h-4 w-4 text-success" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-success">{stats?.converted ?? "—"}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Taux conversion</CardTitle>
              <PercentIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats != null ? `${stats.conversionRate}%` : "—"}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Crédits versés</CardTitle>
              <Coins className="h-4 w-4 text-warning" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats != null ? stats.totalBonusCreditsPaid.toLocaleString("fr-FR") : "—"}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter + Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Liste des parrainages</CardTitle>
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v as ReferralStatus | "ALL");
                setPage(1);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Tous les statuts</SelectItem>
                <SelectItem value="PENDING">En attente</SelectItem>
                <SelectItem value="CONVERTED">Convertis</SelectItem>
                <SelectItem value="EXPIRED">Expirés</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Parrain</TableHead>
                  <TableHead>Filleul</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Date inscription</TableHead>
                  <TableHead>1er achat</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Crédits versés</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                      Aucun parrainage trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((r) => (
                    <TableRow key={r.id} className={isFetching ? "opacity-60" : undefined}>
                      <TableCell className="font-medium">{r.referrerName}</TableCell>
                      <TableCell>{r.refereeName}</TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{r.referralCode}</code>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDate(r.referredAt)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDate(r.firstPurchaseAt)}</TableCell>
                      <TableCell>{statusBadge(r.status as ReferralStatus)}</TableCell>
                      <TableCell className="text-right font-medium">
                        {r.bonusCreditsPaid > 0 ? (
                          <span className="text-success">{r.bonusCreditsPaid.toLocaleString("fr-FR")}</span>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <span className="text-sm text-muted-foreground">
                  Page {page} / {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
